import * as Sentry from '@sentry/react';
import {
  AUTH_DELETE_TOKEN,
  AUTH_SIGN_IN,
  USER_REMOVE_DETAILS,
  USER_REMOVE_PROFILE_SETTINGS,
  VIDEO_RESET
} from 'common/constants/action-types';

import axios from 'axios';
import {
  API_URL,
  DEFAULT_TOKEN_EXPIRATION_TIME,
  ERROR_SUBCODE_WRONG_CREDENTIALS,
  NETWORK_MAX_RETRIES,
  NETWORK_RETRY_TIMEOUT_INIT, ROUTE_LOGOUT
} from 'common/config/constants';
import {
  deleteLastWatchedChannelId,
  deleteTimeToLockChannels,
  deleteTokenObject,
  deleteUserObject,
  getTokenObject,
  getUserObject,
  hasCredentialsObject, setTokenObject,
  setUserObject
} from 'common/services/helpers';
import request from 'common/services/request';
import { isEmpty } from 'lodash';

const populateTokenObject = (tokenObject) => {
  return {
    ...tokenObject,
    expires_at: Date.now() / 1000 + (tokenObject?.expires_in || DEFAULT_TOKEN_EXPIRATION_TIME)
  }
}

const signInMaybe = async (credentials, googleTokenObj, dispatch) => {
  if (googleTokenObj === undefined) {
    const localClient = axios.create({ baseURL: API_URL });
    const { data: { data: tokenObj } } = await localClient({
      method: 'post',
      url: '/users/login',
      data: credentials
    });
    const tokenObject = populateTokenObject(tokenObj);
    dispatch({
      type: AUTH_SIGN_IN,
      payload: tokenObject
    });
    setUserObject(credentials);
    if (!isEmpty(tokenObject?.access_token)) {
      setTokenObject(tokenObject);
    }
    return tokenObject;
  } else {
    dispatch({
      type: AUTH_SIGN_IN,
      payload: googleTokenObj
    });

    return googleTokenObj;
  }
};

export const signIn = (credentials, googleTokenObj) => {
  return async (dispatch) => {
    for (let i = 0; i < NETWORK_MAX_RETRIES; i++) {
      try {
        return await signInMaybe(credentials, googleTokenObj, dispatch);
      } catch (error) {
        if (error.code === 'ERR_NETWORK' && i !== NETWORK_MAX_RETRIES - 1) {
          await new Promise((res) => setTimeout(res, NETWORK_RETRY_TIMEOUT_INIT * 2 ** i));

          continue;
        }
        // Send the error to the component
        // that has triggered the action.
        // This gives the opportunity to
        // display specific server errors
        // in components without using
        // the redux store.
        console.warn(error);
        if (error.response?.data?.subcode === ERROR_SUBCODE_WRONG_CREDENTIALS) {
          deleteUserObject();
        }
        return Promise.reject(error);
      }
    }
  };
};


export const refreshTokenMaybe = async (dispatch) => {
  try {
    const localClient = axios.create({ baseURL: API_URL });
    const currentTokenObj = getTokenObject();
    const refreshToken = currentTokenObj?.refreshToken;
    if (isEmpty(refreshToken)) {
      throw new Error('No refresh token available');
    }
    const credentials = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    }
    const { data: { data: tokenObj } } = await localClient({
      method: 'post',
      url: '/users/refresh',
      data: credentials,
      headers: {
        Authorization: `Bearer ${currentTokenObj?.access_token}`,
      }
    });
    if (isEmpty(tokenObj?.refreshToken)) {
      throw new Error('No refresh token returned from server');
    }
    console.info('[AUTH] Token refreshed successfully');
    const tokenObject = populateTokenObject(tokenObj);
    dispatch({
      type: AUTH_SIGN_IN,
      payload: tokenObject
    });
    setTokenObject(tokenObject);
    return tokenObject;
  } catch (error) {
    console.warn('[AUTH] Refreshing token failed', error);
    return false;
  }
}

export const signUp = (signUpData) => {
  return async (dispatch) => {
    try {
      const response = await request({
        method: 'post',
        url: '/users',
        data: signUpData
      });
      // Allow the invoking component to
      // know when the request has succeeded
      // and render the post registration
      // info.
      return Promise.resolve(response);
    } catch (error) {
      // Send the error to the component
      // that has triggered the action.
      // This gives the opportunity to
      // display specific server errors
      // in components without using
      // the redux store.
      return Promise.reject(error);
    }
  };
};

export const testGeoRestriction = () => {
  return async (dispatch) => {
    try {
      const response = await request({
        method: 'get',
        url: '/geo'
      });
      // Allow the invoking component to
      // know when the request has succeeded
      // and render the post registration
      // info.
      return Promise.resolve(response);
    } catch (error) {
      // Send the error to the component
      // that has triggered the action.
      // This gives the opportunity to
      // display specific server errors
      // in components without using
      // the redux store.
      return Promise.reject(error);
    }
  };
};

export const requestChangePasswordLink = (email) => {
  return async (dispatch) => {
    try {
      const trimmedEmail = email.replace(/\s/g, '');
      const response = await request({
        method: 'get',
        url: `users/resetpassword?email=${trimmedEmail}`
      });
      // Allow the invoking component to
      // know when the request has succeeded
      // and render the appropriate info.
      return Promise.resolve(response);
    } catch (error) {
      // Send the error to the component
      // that has triggered the action.
      // This gives the opportunity to
      // display specific server errors
      // in components without using
      // the redux store.
      return Promise.reject(error);
    }
  };
};

export const resetPassword = (data) => {
  return async (dispatch) => {
    try {
      const response = await request({
        method: 'post',
        url: 'users/resetpassword',
        data
      });
      // Allow the invoking component to
      // know when the request has succeeded
      // and render the appropriate info.
      return Promise.resolve(response);
    } catch (error) {
      // Send the error to the component
      // that has triggered the action.
      // This gives the opportunity to
      // display specific server errors
      // in components without using
      // the redux store.
      return Promise.reject(error);
    }
  };
};

export const changePassword = (oldPassword, newPassword, newPasswordRepeat) => {
  return async (dispatch) => {
    const response = await request({
      method: 'post',
      url: '/users/me/changepassword',
      data: {
        oldpass: oldPassword,
        password: newPassword,
        repeatPassword: newPasswordRepeat
      }
    });
    // Update the user object in the local storage
    const currentUserObject = getUserObject();
    setUserObject({ ...currentUserObject, password: newPassword });
    //
    // Allow the invoking component to
    // know when the request has succeeded
    // and render the appropriate info.
    return response;
  };
};

export const signOut = (redirect = null) => {
  return async (dispatch) => {
    try {
      deleteUserObject();
      dispatch({
        type: AUTH_DELETE_TOKEN,
        payload: {
          redirect
        }
      });

      try {
        await request({
          method: 'post',
          url: ROUTE_LOGOUT
        });
      } catch (error) {
        console.warn('[AUTH] Sign out call failed, signing out anyway...', error);
      }

      dispatch({
        type: USER_REMOVE_DETAILS,
        payload: {}
      });

      dispatch({
        type: VIDEO_RESET,
        payload: null
      });

      dispatch({
        type: USER_REMOVE_PROFILE_SETTINGS,
        payload: {}
      });
      Sentry.setUser(null);
      // deleteTrialObject();
      deleteTokenObject();
      deleteLastWatchedChannelId();
      deleteTimeToLockChannels();
    } catch (error) {
      console.warn(error);
    }
  };
};

export const checkUserToken = async (access_token) => {
  try {
    // Use local Axios client
    const localClient = axios.create({ baseURL: API_URL });

    await localClient({
      method: 'get',
      url: '/users/check',

      headers: {
        Authorization: `Bearer ${access_token}`
      }
    });
    return true;
  } catch (error) {
    console.warn(error);
    return false;
  }
};

export const checkUserSubscription = () => {
  return async (dispatch) => {
    try {
      const response = await request({
        method: 'get',
        // url: '/users/me/activesubscription'
        url: '/users/me/basicsubscription'
      });
      return response
    } catch (error) {
      console.warn(error);
      return false;
    }
  }
}

const handleRefresh = async (onRefresh) => {
  try {
    onRefresh && (await onRefresh());
    return true;
  } catch (error) {
    // Do not clear user and token data on error for onRefresh callback
    console.warn(error);
    return false;
  }
};

export const refreshToken = (onRefresh) => {
  return async (dispatch) => {
    try {
      const userObject = getUserObject();
      const tokenObject = getTokenObject();
      // First check if the token is still valid or near valid
      // Also check if field `expires_at` is present in the token object, if not, consider it expired
      if (tokenObject?.expires_at
        && tokenObject.expires_at - Date.now() / 1000 > 60
        && await checkUserToken(tokenObject?.access_token)) {

        console.info('[AUTH] Token is valid');
        return handleRefresh(onRefresh);
      }
      // Try to refresh the token using the refresh token
      else if (await refreshTokenMaybe(dispatch)) {
        return handleRefresh(onRefresh);
      }
      // Fallback to signing in again using the stored credentials
      else if (hasCredentialsObject(userObject)) {
        // Auto sign in to refresh token
        await dispatch(signIn({ ...userObject, requestRefreshToken: true }));
        // 
        return handleRefresh(onRefresh);
      } else if (await checkUserToken(tokenObject?.access_token)) {
        console.info('[AUTH] User has logged in with valid token, continue...');
        return handleRefresh(onRefresh);
      } else {
        throw new Error('[AUTH] Invalid credentials');
      }
    } catch (error) {
      console.warn('[AUTH] Refreshing token failed', error);
      deleteTokenObject();
      return false;
    }
  };
};


export const setAuthToken = (authToken, refreshToken) => {
  return async (dispatch) => {
    const tokenObj = populateTokenObject({ access_token: authToken, refreshToken });
    try {
      const validToken = await checkUserToken(authToken);
      if (validToken) {
        dispatch({
          type: AUTH_SIGN_IN,
          payload: tokenObj
        });
        setTokenObject(tokenObj);
        deleteUserObject();
        return true;
      } else {
        throw new Error('Invalid auth token');
      }
    } catch (error) {
      console.warn('[AUTH] Setting auth token failed', error);
      deleteTokenObject();
      return false;
    }
  };
};